import {
  Table as TableMaterial,
  TableContainer,
  TableBody,
  TableRow,
  TablePagination,
  TableHead,
  TableSortLabel,
} from '@mui/material';
import { memo, useCallback } from 'react';
import { Fragment } from 'react';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';
import { OrderTypes } from 'shell/constants/order-types';
import { Order } from 'shell/view-models/order';
import { requestSort } from 'shell/utils/request-sort';
import { useOrder } from 'shell/hooks/use-order';
import { PmiTableRow } from 'shell/components/shared/table/row';
import { PmiHeadTableCell } from 'shell/components/shared/table-custom/head';
import { PmiBodyTableCell } from 'shell/components/shared/table-custom/body';

interface TableProps<T extends BaseTableRows, K extends keyof T> {
  head: Array<HeadCell<T, K>>;
  count: number;
  body: Array<T>;
  page?: number;
  rowsPerPage?: number;
  orders?: Order;
  ariaLabel: string;
  onSort?: (orders?: Order) => void;
  onChangePage: (page?: number) => void;
}

const TableTerritoryComponent = <T extends BaseTableRows, K extends keyof T>({
  body = [],
  head,
  count,
  page = 0,
  rowsPerPage = 10,
  onChangePage,
  onSort = () => {
    return;
  },
  orders,
  ariaLabel,
}: TableProps<T, K>): React.ReactElement => {
  const existOrderFunction = useOrder<T, K>(orders || {});

  const handleRequestSort = useCallback(
    (property: K) => {
      const newOrders = requestSort(property, orders);
      onSort(newOrders);
    },
    [orders]
  );

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      onChangePage(newPage);
    },
    [onChangePage]
  );

  const firstItem = page * rowsPerPage;
  const lastItem = firstItem + rowsPerPage;

  return (
    <Fragment>
      <TableContainer>
        <TableMaterial
          sx={{ minWidth: 950 }}
          size="medium"
          stickyHeader
          aria-label={ariaLabel}
          style={{ tableLayout: 'fixed' }}
        >
          <TableHead>
            <TableRow>
              {head.map((headCell) => {
                const { exist: existOrder, value } = existOrderFunction(headCell);
                return headCell.label ? (
                  <PmiHeadTableCell
                    key={headCell.id.toString()}
                    align="left"
                    sortDirection={!!existOrder ? value || OrderTypes.Asc : false}
                  >
                    <TableSortLabel
                      active={false}
                      disabled
                      direction={!!existOrder ? value : OrderTypes.Asc}
                      onClick={() => handleRequestSort(headCell.id as K)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </PmiHeadTableCell>
                ) : (
                  <PmiHeadTableCell key={headCell.id.toString()} align="left"></PmiHeadTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {body.slice(firstItem, lastItem).map((row, bodyItemIndex: number) => {
              const id = `table-row-${bodyItemIndex}`;

              return (
                <PmiTableRow key={id}>
                  {head.map((column, headItemIndex) => {
                    const labelId = `table-${row[column.id]}-${headItemIndex}`;
                    if (column.label) {
                      return (
                        <PmiBodyTableCell key={labelId} id={labelId} scope="row">
                          {row[column.id]}
                        </PmiBodyTableCell>
                      );
                    }
                    return null;
                  })}
                </PmiTableRow>
              );
            })}
          </TableBody>
        </TableMaterial>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[10]}
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      ></TablePagination>
    </Fragment>
  );
};

TableTerritoryComponent.displayName = 'Table territory component';

const TableTerritory = memo(TableTerritoryComponent);
export default TableTerritory as typeof TableTerritoryComponent;
